import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@drpcrd/utils/shared";
import { HTMLAttributes, ElementType } from "react";

const typographyVariants = cva("", {
  variants: {
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
    },
    letterSpacing: {
      normal: "tracking-normal",
      wide: "!tracking-widest",
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
      light: "font-light",
      semibold: "font-semibold",
      bold: "font-bold",
      extrabold: "font-extrabold",
    },
    size: {
      xs: "text-xs tracking-wide uppercase",
      sm: "text-sm tracking-normal",
      base: "text-sm sm:text-base tracking-normal",
      lg: "text-base sm:text-lg tracking-normal",
      xl: "text-lg sm:text-xl tracking-normal",
      "2xl": "text-xl sm:text-2xl tracking-normal",
      "3xl": "text-3xl sm:text-4xl tracking-normal",
      "4xl": "text-2xl lg:text-4xl tracking-normal",
    },
    color: {
      primary: "text-text-body",
      neutral: "text-text-body-secondary",
      action: "text-text-action",
      disabled: "text-text-disabled",
      error: "text-text-error",
      white: "text-white",
      black: "text-black",
      inherit: "text-inherit",
    },
  },
  defaultVariants: {
    weight: "normal",
    letterSpacing: "normal",
    size: "base",
    color: "primary",
    align: "left",
  },
});

type TypographyProps = VariantProps<typeof typographyVariants> &
  HTMLAttributes<HTMLElement> & {
    as?: ElementType;
    truncate?: boolean;
  };

/**
 * Typography component for consistent text styling across the application
 */
export const Typography = ({
  weight,
  letterSpacing,
  size,
  color,
  align,
  truncate,
  className,
  children,
  as: Component = "p",
  ...props
}: TypographyProps) => (
  <Component
    className={cn(
      typographyVariants({ weight, size, color, align, letterSpacing }),
      truncate && "overflow-hidden text-ellipsis whitespace-nowrap",
      !truncate && "whitespace-pre-line",
      className,
    )}
    {...props}
  >
    {children}
  </Component>
);
